import { handleDeleteRequest, handleGetRequest, handlePostRequest, handlePutRequest } from '.';
import { moduleName } from '../../constants';
import { URI } from '../../constants/URI';
import {
    setCustomerBalance,
    setCustomerDetails,
    setCustomerExclusionProducts,
    setCustomerNotes,
    setCustomerPotentialPurchaseItems,
    setCustomerPurchaseItems,
    setCustomers,
    setCustomerSpecialPrices,
    setCustomerSuggestedPrices,
    setCustomerPreferredProducts,
} from '../slices/customersSlice';
import { setCustomer } from '../slices/filtersSlice';
import { store } from '../store';

export const getCustomersWithPagination = async (body) => {
    const res = await handlePostRequest(URI.customers, body, {
        module: moduleName.CUSTOMERS,
        loading: false,
    });
    return res;
};

export const getCustomerGlobalSearch = async (body, loading) => {
    const res = await handlePostRequest(URI.customer_global, body, {
        loading,
    });
    return res;
};

export const getCustomers = async () => {
    const res = await handleGetRequest(URI.customers, null, {
        module: moduleName.CUSTOMERS,
    });

    if (res?.ack) {
        store.dispatch(setCustomers(res.customers));
        res?.customers.length === 1 &&
            store.dispatch(setCustomer(res.customers[0].customer_netsuite_id));
    }

    return res;
};

export const getCustomerDetail = async (customer_netsuite_id) => {
    const res = await handleGetRequest(URI.customer_detail, null, {
        module: moduleName.CUSTOMERS,
        params: { customer_netsuite_id },
    });
    if (res?.ack) {
        store.dispatch(setCustomerDetails(res?.customer_details));
        store.dispatch(setCustomerBalance(res?.customer_balance));
    }
    return res;
};

export const getCustomerSpecialPrices = async (customer_netsuite_id) => {
    const res = await handleGetRequest(URI.customer_special_prices, null, {
        module: moduleName.CUSTOMERS,
        params: { customer_netsuite_id },
        loading: false,
        toast: false,
    });
    store.dispatch(setCustomerSpecialPrices(res?.special_prices || []));
    return res;
};

export const deleteCustomerSpecialPrices = async (body) => {
    const res = await handleDeleteRequest(URI.customer_special_prices, body);
    return res;
};

export const getCustomerSuggestedPrices = async (customer_netsuite_id) => {
    const res = await handleGetRequest(URI.customer_suggested_prices, null, {
        params: { customer_netsuite_id },
        loading: false,
    });
    store.dispatch(setCustomerSuggestedPrices(res?.suggested_prices || []));
    return res;
};

export const addCustomerSuggestedPrice = async (body) => {
    const res = await handlePostRequest(URI.customer_suggested_prices, body, {
        module: moduleName.CUSTOMERS,
    });
    if (res?.ack) getCustomerSuggestedPrices(body?.customer_netsuite_id);
    return res;
};

export const updateCustomerSuggestedPrice = async (body) => {
    const res = await handlePutRequest(URI.customer_suggested_prices, body, {
        module: moduleName.CUSTOMERS,
    });
    if (res?.ack) getCustomerSuggestedPrices(body?.customer_netsuite_id);
    return res;
};

export const deleteCustomerSuggestedStatus = async (body) => {
    const res = await handleDeleteRequest(URI.customer_suggested_prices, body, {
        module: moduleName.CUSTOMERS,
    });
    return res;
};

export const getCustomerPurchasedProducts = async (body) => {
    const res = await handlePostRequest(URI.customer_purchased_products, body, {
        module: moduleName.CUSTOMERS,
        loading: false,
    });
    store.dispatch(setCustomerPurchaseItems(res?.purchased_products || []));
    return res;
};

export const getCustomerSuggestedProducts = async (body) => {
    const res = await handlePostRequest(URI.customer_suggested_products, body, {
        module: moduleName.CUSTOMERS,
        loading: false,
        toast: false,
    });
    store.dispatch(setCustomerPotentialPurchaseItems(res?.suggested_products || []));
    return res;
};

// Update status of suggested price
export const updateCustomerSuggestedStatus = async (body) => {
    const res = await handlePutRequest(URI.customer_suggested_price_status, body, {
        module: moduleName.CUSTOMERS,
    });
    return res;
};

export const syncCustomers = async () => {
    const res = await handlePostRequest(URI.sync_customers);
    return res;
};

export const addCustomerNotes = async (body) => {
    const res = await handlePostRequest(URI.customer_notes, body);
    return res;
};

export const getCustomerNotes = async (customer_netsuite_id) => {
    const res = await handleGetRequest(URI.customer_notes, null, {
        params: { customer_netsuite_id },
    });
    if (res?.ack) {
        store.dispatch(setCustomerNotes(res?.notes));
    }
    return res;
};

export const updateCustomerNotes = async (body) => {
    const res = await handlePutRequest(URI.customer_notes, body);
    return res;
};

export const deleteCustomerNotes = async (body) => {
    const res = await handleDeleteRequest(URI.customer_notes, body);
    return res;
};

// Exclusion Products
export const getCustomerExclusionProducts = async (body) => {
    const res = await handlePostRequest(URI.customer_exclusion_products, body, {
        module: moduleName.CUSTOMERS,
    });
    store.dispatch(setCustomerExclusionProducts(res?.customer_exclusion_products || []));
    return res;
};

export const getCustomerExclusionProduct = async (id) => {
    const res = await handleGetRequest(URI.customer_exclusion_product, null, {
        module: moduleName.CUSTOMERS,
        params: { id },
    });
    return res;
};

export const addExclusionProduct = async (body) => {
    const res = await handlePostRequest(URI.customer_exclusion_product, body, {
        module: moduleName.CUSTOMERS,
    });
    return res;
};

export const updateExclusionProduct = async (body) => {
    const res = await handlePutRequest(URI.customer_exclusion_product, body, {
        module: moduleName.CUSTOMERS,
    });
    return res;
};

export const deleteExclusionProduct = async (body) => {
    const res = await handleDeleteRequest(URI.customer_exclusion_product, body, {
        module: moduleName.CUSTOMERS,
    });
    if (res?.ack) getCustomerExclusionProducts({ customer_netsuite_id: body?.customer_netsuite_id });
    return res;
};

// Preferred Products
export const getCustomerPreferredProducts = async (body, loading = true) => {
    const res = await handlePostRequest(URI.customer_preferred_products, body, {
        module: moduleName.CUSTOMERS,
        loading,
    });
    store.dispatch(setCustomerPreferredProducts(res?.customer_preferred_products || []));
    return res;
};

export const getCustomerPreferredProduct = async (id) => {
    const res = await handleGetRequest(URI.customer_preferred_product, null, {
        module: moduleName.CUSTOMERS,
        params: { id },
    });
    return res;
};

export const addPreferredProduct = async (body) => {
    const res = await handlePostRequest(URI.customer_preferred_product, body, {
        module: moduleName.CUSTOMERS,
    });
    return res;
};

export const updatePreferredProduct = async (body) => {
    const res = await handlePutRequest(URI.customer_preferred_product, body, {
        module: moduleName.CUSTOMERS,
    });
    return res;
};

export const deletePreferredProduct = async (body) => {
    const res = await handleDeleteRequest(URI.customer_preferred_product, body, {
        module: moduleName.CUSTOMERS,
    });
    if (res?.ack) getCustomerPreferredProducts({ customer_netsuite_id: body?.customer_netsuite_id });
    return res;
};

export const syncCustomerSpecialPrices = async (body) => {
    const res = await handlePostRequest(URI.sync_customer_prices, body);
    if (res?.ack) {
        getCustomerSpecialPrices(body?.customer_netsuite_id);
    }
    return res;
};

export const validateCustomerSpecialPrice = async (body) => {
    const res = await handlePostRequest(URI.validate_customer_special, body, {
        module: moduleName.CUSTOMERS,
        loading: false,
        toast: false,
    });
    return res;
};

export const getCustomerDeliveryDays = async (customer_netsuite_id) => {
    const res = await handleGetRequest(URI.customer_delivery_days, null, {
        module: moduleName.CUSTOMERS,
        params: { customer_netsuite_id },
        loading: false,
        toast: false,
    });
    return res;
};

// Call List
export const getCallList = async (body) => {
    const res = await handlePostRequest(URI.call_list, body, {
        toast: false,
    });
    return res;
};

export const addCallListLog = async (body) => {
    const res = await handlePostRequest(URI.call_list_log, body);
    return res;
};

export const getCallListLogs = async (body) => {
    const res = await handlePostRequest(URI.call_list_logs, body, {
        toast: false,
    });
    return res;
};

export const getCallListByCustomer = async (body) => {
    const res = await handlePostRequest(URI.call_list_by_customer, body, {
        toast: false,
    });
    return res;
};

// Notifications
export const getCustomerPendingPrices = async () => {
    const res = await handleGetRequest(URI.check_customer_pending_prices, null, {
        toast: false,
        loading: false,
    });
    return res;
};

export const getPendingPrices = async (body) => {
    const res = await handlePostRequest(URI.customer_pending_prices, body)
    return res;
}

export const getCustomerStatementLink = async (body) => {
    const res = await handlePostRequest(URI.customer_statement, body);
    return res;
}